import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { MODULES, TILES } from './Home';

type HeaderLayout = 'top' | 'side';

const MODULE_ICONS: Record<string, string> = {
  'Configuração': '/assets/img/icons/settings',
  'Ferramentas com IA': '/assets/img/icons/sap-logs',
  'Repositórios e modelos': '/assets/img/icons/benchmark-perfis-sap',
  'Redesenho de perfis': '/assets/img/icons/profilegen',
  'Compliance Owner - Tools': '/assets/img/icons/cowner-cockpit'
};

const normalizePath = (path: string) => {
  const normalized = (path || '/').trim();
  if (normalized.length > 1 && normalized.endsWith('/')) return normalized.slice(0, -1);
  return normalized || '/';
};

const SPA_LINKS = new Set([
  '/home',
  '/users',
  '/profiles',
  '/integrations',
  '/global-clients',
  '/job-monitor',
  '/benchmark-perfis-sap',
  '/gerador-perfis',
  '/analisador-logs-sap',
  '/action-orglevel',
  '/action-risk',
  '/cowner-cockpit',
  '/cowner-admin-clientes',
  '/sap-controls-repository',
  '/ads',
]);

const isSpaLink = (path: string) => SPA_LINKS.has(normalizePath(path));

const canAccessTile = (tileLink: string, allowedLinks: string[] | null) => {
  if (allowedLinks === null) return false;
  if (allowedLinks.includes(tileLink)) return true;
  // Backward-compatible fallback: ADS follows existing Redesenho permissions
  if (tileLink === '/ads') {
    return allowedLinks.includes('/gerador-perfis') || allowedLinks.includes('/analisador-logs-sap');
  }
  return false;
};

const resolveApiBase = () => {
  const baseEnv = process.env.REACT_APP_API_URL || '';
  const hostname = window.location.hostname;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) return '';
  if (!baseEnv) return '';
  try {
    const envUrl = new URL(baseEnv);
    if (envUrl.origin === window.location.origin) return '';
    return baseEnv.replace(/\/$/, '');
  } catch {
    return baseEnv.replace(/\/$/, '');
  }
};

const Header: React.FC<{ theme: string; toggleTheme: () => void }> = ({ theme, toggleTheme }) => {
  const navbarTheme: 'dark' = 'dark';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();
  const [headerLayout, setHeaderLayout] = useState<HeaderLayout>(() => {
    const saved = localStorage.getItem('phoenixHeaderLayout');
    if (saved === 'top' || saved === 'side') return saved;
    return 'side';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => localStorage.getItem('phoenixSidebarCollapsed') === 'true');
  const handlePhotoClick = () => setMenuOpen((open) => !open);
  const { userId } = useAuth();
  const [user, setUser] = useState<any | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // modules menu state
  const [allowedLinks, setAllowedLinks] = useState<string[] | null>(null);
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [mobileModulesOpen, setMobileModulesOpen] = useState(false);
  const toggleHeaderLayout = () => setHeaderLayout((prev) => (prev === 'top' ? 'side' : 'top'));
  const toggleSidebarCollapsed = () => {
    setOpenModule(null);
    setSidebarCollapsed((prev) => !prev);
  };
  const currentPath = normalizePath(location.pathname || '/');
  const isSidebarCollapsed = headerLayout === 'side' && sidebarCollapsed;
  const headerReady = userLoaded && permissionsLoaded;

  useEffect(() => {
    const urlBase = resolveApiBase();
    let mounted = true;
    setUserLoaded(false);
    (async () => {
      try {
        if (!userId) {
          if (!mounted) return;
          setUser(null);
          setProfileName(null);
          return;
        }
        const res = await axios.get(`${urlBase}/api/users/${userId}`, { withCredentials: true });
        if (!mounted) return;
        setUser(res.data || null);
        const profileField = res.data?.profile;
        if (profileField) {
          // profile may be populated (object) or just an id (string)
          if (typeof profileField === 'object' && profileField !== null) {
            setProfileName(profileField.name || profileField._id || null);
          } else if (typeof profileField === 'string') {
            try {
              const p = await axios.get(`${urlBase}/api/profiles/${profileField}`, { withCredentials: true });
              if (!mounted) return;
              setProfileName(p.data?.name || null);
            } catch (e) {
              // Profile not found or API error - silently ignore
              // This can happen if the profile was deleted but still referenced in user data
              if (!mounted) return;
              // Optionally set profile name to the ID so user knows something is linked
              // setProfileName(profileField); // uncomment to show ID when profile is deleted
            }
          }
        }
      } catch (e) {
        console.error('Erro ao carregar usuário', e);
      } finally {
        if (mounted) setUserLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  // Load allowed applications for current user (to populate header module menus)
  useEffect(() => {
    const urlBase = resolveApiBase();
    let mounted = true;
    setPermissionsLoaded(false);
    (async () => {
      try {
        if (!userId) { if (mounted) setAllowedLinks([]); return; }
        const userRes = await axios.get(`${urlBase}/api/users/${userId}`, { withCredentials: true });
        const rawProfile = userRes.data?.profile;
        const profileId = typeof rawProfile === 'string' ? rawProfile : rawProfile?._id;
        if (!profileId) { if (mounted) setAllowedLinks([]); return; }
        const [appsRes, paRes] = await Promise.all([
          axios.get(`${urlBase}/api/applications`, { withCredentials: true }),
          axios.get(`${urlBase}/api/profile-apps`, { withCredentials: true })
        ]);
        const apps: any[] = Array.isArray(appsRes.data) ? appsRes.data : [];
        const links: any[] = Array.isArray(paRes.data) ? paRes.data : [];
        const allowedAppIds = links
          .filter((l) => {
            const linkProfileId = typeof l.profile === 'string' ? l.profile : l.profile?._id;
            return linkProfileId === profileId;
          })
          .map((l) => (typeof l.application === 'string' ? l.application : (l.application?._id)));
        const allowedAppNames = apps.filter(a => allowedAppIds.includes(a._id)).map(a => a.name);
        if (mounted) setAllowedLinks(allowedAppNames);
      } catch (e) {
        console.error('Erro ao carregar aplicações para header', e);
        if (mounted) setAllowedLinks([]);
      } finally {
        if (mounted) setPermissionsLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  useEffect(() => {
    document.body.classList.toggle('header-initializing', !headerReady);
    return () => {
      document.body.classList.remove('header-initializing');
    };
  }, [headerReady]);

  // grouped tiles by module (reusing TILES)
  const grouped = useMemo(() => {
    const g = new Map<string, typeof TILES>();
    for (const t of TILES) {
      if (!g.has(t.module)) g.set(t.module, [] as any);
      g.get(t.module)!.push(t as any);
    }
    return g;
  }, []);

  const orderedModules = useMemo(() => {
    const present = Array.from(grouped.keys());
    const defined = MODULES.filter(m => present.includes(m.name));
    const remaining = present.filter(name => !MODULES.some(m => m.name === name)).sort().map(name => ({ name, description: '' }));
    return [...defined, ...remaining];
  }, [grouped]);
  const userDisplayName = user?.fullName || user?.username || 'Usuário';
  const userInitials = userDisplayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase() || '')
    .join('') || 'U';

  const isPathActive = useCallback((targetPath: string) => {
    const target = normalizePath(targetPath);
    if (target === '/home') return currentPath === '/home' || currentPath === '/';
    return currentPath === target || currentPath.startsWith(`${target}/`);
  }, [currentPath]);

  const activeModuleName = useMemo(() => {
    for (const { name } of orderedModules) {
      const tiles = grouped.get(name) || [];
      const visibleTiles = tiles.filter((tile) => {
        return canAccessTile(tile.link, allowedLinks);
      });
      if (visibleTiles.some((tile) => isPathActive(tile.link))) {
        return name;
      }
    }
    return null;
  }, [orderedModules, grouped, allowedLinks, isPathActive]);

  useEffect(() => {
    if (headerLayout !== 'side' || sidebarCollapsed) return;
    if (!activeModuleName) return;
    setOpenModule(activeModuleName);
  }, [headerLayout, activeModuleName, sidebarCollapsed]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      const base = resolveApiBase();
      // Use fetch para chamar a API de logout e garantir que a sessão no servidor seja destruída
      await fetch(`${base}/api/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
    } catch (err) {
      // Se a chamada falhar, ainda assim redirecionamos para a tela de login
      // (fallback para garantir UX consistente)
      console.error('Logout API error:', err);
    }
    // Forçar recarregamento completo para garantir que o estado local do SPA seja reinicializado
    // Isso evita que o usuário consiga acessar rotas protegidas apenas por estado em memória.
    try {
      // Navega para /login e força reload completo da página
      window.location.replace('/login');
      setTimeout(() => window.location.reload(), 100);
    } catch (e) {
      window.location.href = '/login';
    }
  };
  React.useEffect(() => {
    const closeMenu = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.user-photo') && !(e.target as HTMLElement).closest('.user-menu')) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', closeMenu);
    }
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [menuOpen]);
  // close module dropdown when clicking outside
  React.useEffect(() => {
    const closeModule = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.header-modules') ) {
        setOpenModule(null);
      }
    };
    if (openModule && (headerLayout === 'top' || (headerLayout === 'side' && sidebarCollapsed))) {
      document.addEventListener('mousedown', closeModule);
    }
    return () => document.removeEventListener('mousedown', closeModule);
  }, [openModule, headerLayout, sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('phoenixHeaderLayout', headerLayout);
    localStorage.setItem('phoenixSidebarCollapsed', String(sidebarCollapsed));
    document.body.classList.toggle('layout-header-side', headerLayout === 'side');
    document.body.classList.toggle('layout-header-side-collapsed', headerLayout === 'side' && sidebarCollapsed);
    document.body.setAttribute('data-header-layout', headerLayout);
    return () => {
      document.body.classList.remove('layout-header-side');
      document.body.classList.remove('layout-header-side-collapsed');
      document.body.removeAttribute('data-header-layout');
    };
  }, [headerLayout, sidebarCollapsed]);

  const renderModuleMenus = () => (
    <>
      {orderedModules.map(({ name }) => {
        const tiles = grouped.get(name) || [];
        const visibleTiles = tiles.filter(t => {
          return canAccessTile(t.link, allowedLinks);
        });
        if (visibleTiles.length === 0) return null;
        const isOpen = openModule === name;
        const moduleHasActiveApp = visibleTiles.some(vt => isPathActive(vt.link));
        return (
          <div key={name} className={`header-module${isOpen ? ' open' : ''}`} style={{ position: 'relative' }}>
            <button className={`nav-link${moduleHasActiveApp ? ' is-active' : ''}${isSidebarCollapsed ? ' nav-link--icon-only' : ''}`} onClick={() => setOpenModule(isOpen ? null : name)} aria-haspopup="true" aria-expanded={isOpen} title={isSidebarCollapsed ? name : undefined}>
              {MODULE_ICONS[name] && (
                <img
                  src={navbarTheme === 'dark' ? `${MODULE_ICONS[name]}-dark.svg` : `${MODULE_ICONS[name]}-light.svg`}
                  alt=""
                  className="header-module-icon"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!target.src.includes('fallback')) {
                      target.src = `${MODULE_ICONS[name]}.svg?fallback=true`;
                    }
                  }}
                />
              )}
              {!isSidebarCollapsed && <span>{name}</span>}
              <span className={`module-arrow ${headerLayout === 'side' ? 'module-arrow--side' : 'module-arrow--top'}${isOpen ? ' is-open' : ''}`} aria-hidden>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                  <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
            {isOpen && (headerLayout === 'top' || (headerLayout === 'side' && sidebarCollapsed)) && (
              <div className={`header-module-dropdown${navbarTheme === 'dark' ? ' header-module-dropdown--dark' : ''}${headerLayout === 'side' && sidebarCollapsed ? ' header-module-dropdown--side-collapsed' : ''}`} role="menu">
                {visibleTiles.map((vt) => {
                  const itemContent = (
                    <>
                      {vt.icon && (
                        <img
                          src={navbarTheme === 'dark' ? `${vt.icon}-dark.svg` : `${vt.icon}-light.svg`}
                          alt=""
                          style={{ width: '20px', height: '20px', flexShrink: 0 }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.src.includes('fallback')) {
                              target.src = `${vt.icon}.svg?fallback=true`;
                            }
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>{vt.title}</span>
                    </>
                  );

                  if (isSpaLink(vt.link)) {
                    return (
                      <Link
                        key={vt.link}
                        to={vt.link}
                        className={`user-menu__item${isPathActive(vt.link) ? ' is-active' : ''}`}
                        role="menuitem"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                        onClick={() => setOpenModule(null)}
                      >
                        {itemContent}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={vt.link}
                      href={vt.link}
                      className={`user-menu__item${isPathActive(vt.link) ? ' is-active' : ''}`}
                      role="menuitem"
                      style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                      onClick={() => setOpenModule(null)}
                    >
                      {itemContent}
                    </a>
                  );
                })}
              </div>
            )}
            {headerLayout === 'side' && !sidebarCollapsed && (
              <div
                className={`header-module-inline header-module-inline-collapsible${isOpen ? ' is-open' : ''}${navbarTheme === 'dark' ? ' header-module-inline--dark' : ''}`}
                role="menu"
                aria-hidden={!isOpen}
              >
                {visibleTiles.map((vt) => {
                  const itemContent = (
                    <>
                      {vt.icon && (
                        <img
                          src={navbarTheme === 'dark' ? `${vt.icon}-dark.svg` : `${vt.icon}-light.svg`}
                          alt=""
                          style={{ width: '18px', height: '18px', flexShrink: 0 }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.src.includes('fallback')) {
                              target.src = `${vt.icon}.svg?fallback=true`;
                            }
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>{vt.title}</span>
                    </>
                  );

                  if (isSpaLink(vt.link)) {
                    return (
                      <Link
                        key={vt.link}
                        to={vt.link}
                        className={`header-module-inline__item${isPathActive(vt.link) ? ' is-active' : ''}`}
                        role="menuitem"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                        onClick={() => setOpenModule(null)}
                      >
                        {itemContent}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={vt.link}
                      href={vt.link}
                      className={`header-module-inline__item${isPathActive(vt.link) ? ' is-active' : ''}`}
                      role="menuitem"
                      style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                      onClick={() => setOpenModule(null)}
                    >
                      {itemContent}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <header data-bs-theme={navbarTheme} className={`header ${headerLayout === 'side' ? 'header--side' : 'fixed-top'}${isSidebarCollapsed ? ' header--collapsed' : ''} px-4 py-2`}>
      <div className="header-inner d-flex align-items-center w-100 justify-content-between">
        <div className={`d-flex align-items-center header-left${isSidebarCollapsed ? ' header-left--collapsed' : ''}`}>
          <div className={`header-brand${isSidebarCollapsed ? ' header-brand--collapsed' : ''}`}>
            <Link to="/home" aria-label="Ir para Home" title="Início" className="header-logo-link d-flex align-items-center text-decoration-none">
              <img src={isSidebarCollapsed ? '/assets/img/icons/logo_new_mini.svg' : '/assets/img/icons/logo_new.svg'} alt="TrustSis Logo" className={`logo${isSidebarCollapsed ? ' logo--mini' : ''}`} />
            </Link>

            {headerLayout === 'side' && (
              <button
                type="button"
                className={`sidebar-collapse-btn${sidebarCollapsed ? ' is-collapsed' : ''}`}
                onClick={toggleSidebarCollapsed}
                aria-label={sidebarCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
                title={sidebarCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M10 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          {headerLayout === 'side' && (
            <Link to="/home" aria-label="Ir para Home" title="Início" className={`nav-link header-home-link d-flex align-items-center${isPathActive('/home') ? ' is-active' : ''}`}>
              <img src="/assets/img/icons/home-light.svg" alt="home" className="home-icon home-icon--light" />
              <img src="/assets/img/icons/home-dark.svg" alt="home" className="home-icon home-icon--dark" />
              {!isSidebarCollapsed && <span className="home-label">Home</span>}
            </Link>
          )}

          {/* Modules stay in this row only for sidebar layout. */}
          {headerLayout === 'side' && (
            <div className="header-modules d-flex align-items-center">
              {renderModuleMenus()}
            </div>
          )}
  </div>
  {/* Mobile toggle (visible on small screens) */}
        <button
          className="header-mobile-toggle nav-link"
          aria-label="Abrir menu de módulos"
          onClick={() => setMobileModulesOpen(true)}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>☰</span>
        </button>
        <div className="header-right-controls d-flex align-items-center" style={{ position: 'relative' }}>
          {/* Controle de tema Phoenix-like */}
          <div className="theme-control-toggle fa-ion-wait pe-2" style={{ marginRight: '0.7rem' }}>
            <input
              className="form-check-input ms-0 theme-control-toggle-input"
              id="themeControlToggleDoc"
              type="checkbox"
              data-theme-control="phoenixTheme"
              value="dark"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <label
              className="mb-0 theme-control-toggle-label theme-control-toggle-light"
              htmlFor="themeControlToggleDoc"
              title="Switch theme"
            >
              <span className="icon uil uil-moon"></span>
            </label>
            <label
              className="mb-0 theme-control-toggle-label theme-control-toggle-dark"
              htmlFor="themeControlToggleDoc"
              title="Switch theme"
            >
              <span className="icon uil uil-sun"></span>
            </label>
          </div>
          <button
            type="button"
            className="header-layout-toggle"
            onClick={toggleHeaderLayout}
            aria-label={headerLayout === 'top' ? 'Mudar header para lateral' : 'Mudar header para topo'}
            title={headerLayout === 'top' ? 'Header no topo (clique para lateral)' : 'Header lateral (clique para topo)'}
          >
            {headerLayout === 'top' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
                <rect x="3" y="3" width="18" height="4.8" rx="2" fill="currentColor" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
                <rect x="3" y="3" width="4.8" height="18" rx="2" fill="currentColor" />
              </svg>
            )}
          </button>
          <div
            className="rounded-circle user-photo"
            onClick={handlePhotoClick}
            style={{ marginLeft: '0.3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label={`Abrir menu do usuário ${userDisplayName}`}
          >
            <span className="user-photo__initials" aria-hidden="true">{userInitials}</span>
          </div>
          {menuOpen && (
            <div className={`user-menu${navbarTheme === 'dark' ? ' user-menu--dark' : ''}`} role="menu">
              <div className="user-menu__profile">
                <div className="user-menu__name">{user?.fullName || user?.username || 'Usuário'}</div>
                <div className="user-menu__email">{user?.email || ''}</div>
                {profileName && (
                  <div className="user-menu__profile-line">
                    <span className="user-menu__profile-label">Perfil:</span>
                    <span className="user-menu__profile-value"> {profileName}</span>
                  </div>
                )}
              </div>
              <div className="user-menu__item" onClick={handleLogout} role="menuitem">
                <span className="user-menu__icon uil uil-signout"></span>
                Sair
              </div>
            </div>
          )}
        </div>
        {/* Mobile modules menu overlay */}
        {mobileModulesOpen && (
          <div className={`header-mobile-menu${navbarTheme === 'dark' ? ' header-mobile-menu--dark' : ''}`} role="dialog" aria-modal="true">
            <div className="header-mobile-menu__inner">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <strong>Aplicações</strong>
                <button className="nav-link" onClick={() => setMobileModulesOpen(false)} aria-label="Fechar menu">Fechar</button>
              </div>
              <div className="mobile-modules-list">
                {orderedModules.map(({ name }) => {
                  const tiles = grouped.get(name) || [];
                  const visibleTiles = tiles.filter(t => {
                    return canAccessTile(t.link, allowedLinks);
                  });
                  if (visibleTiles.length === 0) return null;
                  return (
                    <div key={name} className="mobile-module">
                      <div className="mobile-module__title">{name}</div>
                      <div className="mobile-module__apps">
                        {visibleTiles.map(vt => {
                          const itemContent = (
                            <>
                              {vt.icon && (
                                <img 
                                  src={navbarTheme === 'dark' ? `${vt.icon}-dark.svg` : `${vt.icon}-light.svg`} 
                                  alt="" 
                                  style={{ width: '18px', height: '18px', flexShrink: 0 }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (!target.src.includes('fallback')) {
                                      target.src = `${vt.icon}.svg?fallback=true`;
                                    }
                                  }}
                                />
                              )}
                              {vt.title}
                            </>
                          );

                          if (isSpaLink(vt.link)) {
                            return (
                              <Link key={vt.link} to={vt.link} className="mobile-app-link" onClick={() => setMobileModulesOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {itemContent}
                              </Link>
                            );
                          }

                          return (
                            <a key={vt.link} href={vt.link} className="mobile-app-link" onClick={() => setMobileModulesOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {itemContent}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="header-mobile-menu__backdrop" onClick={() => setMobileModulesOpen(false)} aria-hidden="true" />
          </div>
        )}
      </div>

      {headerLayout === 'top' && (
        <div className="header-top-modules-row px-4">
          <div className="header-modules d-flex align-items-center">
            <Link to="/home" aria-label="Ir para Home" title="Início" className={`nav-link header-home-link d-flex align-items-center${isPathActive('/home') ? ' is-active' : ''}`}>
              <img src="/assets/img/icons/home-light.svg" alt="home" className="home-icon home-icon--light" />
              <img src="/assets/img/icons/home-dark.svg" alt="home" className="home-icon home-icon--dark" />
              <span className="home-label">Home</span>
            </Link>
            {renderModuleMenus()}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
